import { Button, Grid, Paper, Tooltip, useTheme } from "@mui/material";
import { useState } from "react";
import { Form } from "react-final-form";
import { connect, ConnectedProps } from "react-redux";
import { Navigate } from "react-router";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UrlPathCardDesign } from "../../../api/url";
import { thunkCreateCardDesign, thunkUpdateCardDesign } from "../../../store/actions/CardDesignActions";
import { IStore } from "../../../store/IStore";
import { CardDesignDto } from "../../../store/models/carddesign/CardDesignDto";
import { FormCheckbox } from "../../atoms/FormCheckbox";
import { FormInput } from "../../atoms/FormInput";
import ImsPaperHead from "../../atoms/ImsPaperHead";
import { isBlank, nameof, ValidationErrors } from "../../atoms/Utils";
import { FormSelect } from "../../atoms/FormSelect";
import { Help } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface CardDesignFormProps {
  cardDesign: CardDesignDto;
}

const CardDesignForm = (props: CardDesignFormProps & ThunkProps) => {
  const theme = useTheme();
  const [redirect, setRedirect] = useState(false);
  const { cardDesign, einGeneratorTemplates } = props;
  const { t } = useTranslation();

  const saveCardDesign = async (cardDesignToSave: CardDesignDto) => {
    if (cardDesignToSave.cardDesignNumber) {
      await props.thunkUpdateCardDesign(cardDesignToSave);
    } else {
      await props.thunkCreateCardDesign(cardDesignToSave);
    }
    setRedirect(true);
  };

  if (redirect) {
    return <Navigate to={UrlPathCardDesign} />;
  }

  const validateForm = (values: CardDesignDto) => {
    const errors: ValidationErrors<CardDesignDto> = {};
    if (isBlank(values.name)) {
      errors.name = t("messages.validation.cardDesignNameMandatory");
    }
    if (isBlank(values.externalCardDesignNumber)) {
      errors.externalCardDesignNumber = t("messages.validation.cardDesignNumberMandatory");
    }
    return errors;
  };

  return (
    <Form
      onSubmit={saveCardDesign}
      initialValues={cardDesign}
      validate={validateForm}
      render={({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <Paper
            style={{
              padding: theme.spacing(3),
              marginTop: theme.spacing(6),
            }}
          >
            <ImsPaperHead text={t("setup.headlineCardDesignDetails")} />
            <Grid container spacing={4}>
              <Grid item container spacing={2}>
                <Grid item sm={12}>
                  <FormCheckbox name={nameof<CardDesignDto>("defaultCardDesign")} label={t("setup.standard")} />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name={nameof<CardDesignDto>("name")}
                    label={t("setup.cardDesignDesignation")}
                    readOnly={Boolean(values.cardDesignNumber)}
                  />
                </Grid>

                <Grid item sm={8}></Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name={nameof<CardDesignDto>("externalCardDesignNumber")}
                    label={t("setup.cardDesignId")}
                    readOnly={Boolean(values.cardDesignNumber)}
                  />
                </Grid>
                <Grid item sm={8}></Grid>
                <Grid item sm={4}>
                  <FormSelect
                    name={nameof<CardDesignDto>("templateName")}
                    label={t("setup.cardNumberPattern")}
                    options={[{ label: t("setup.placeholder"), value: "" }].concat(
                      einGeneratorTemplates.map((template) => ({
                        label: template.displayName ? template.displayName : template.name,
                        value: template.name,
                      }))
                    )}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item sm={1}>
                  <Tooltip
                    data-testid="passIdMusterTooltip"
                    title={t("setup.infoCardNumberPattern")}
                    placement="right-start"
                    style={{ marginTop: 16 }}
                  >
                    <Help color="primary" />
                  </Tooltip>
                </Grid>
                <Grid item sm={7}></Grid>
              </Grid>
            </Grid>
          </Paper>
          <Grid container justifyContent="flex-end">
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setRedirect(true)}
              style={{ margin: theme.spacing(2, 0, 2, 2) }}
            >
              {t("setup.buttonCancel")}
            </Button>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              disabled={submitting}
              style={{ margin: theme.spacing(2, 0, 2, 2) }}
            >
              {t("setup.buttonSave")}
            </Button>
          </Grid>
        </form>
      )}
    />
  );
};

const mapStateToProps = (state: IStore) => ({
  einGeneratorTemplates: state.einGeneratorTemplates.einGeneratorTemplates,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkUpdateCardDesign,
      thunkCreateCardDesign,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(CardDesignForm);
